import type { DiagnosticSnapshot, SectionEnvelope } from "@/api/diagnostic";
import type { CoaAction, CoaDiagnosticItem, CoaStatus } from "@/api/diagnostic-chart-of-accounts";
import { formatCount } from "@/lib/format";

/**
 * O quadro "Aderência ao plano de contas" do Ato 1 (#1374 P2, EF-FUNC-009 §8).
 *
 * Fica fora do `diagnosticData.ts` de propósito: é a regra de convivência do
 * épico, cada EF com o seu seletor, para cinco PRs não disputarem o mesmo arquivo.
 *
 * Lê o envelope `chart_of_accounts` do bloco `validation`, e não uma seção: a
 * decisão do consultor é lida ao vivo pelo backend, também com a Calibragem
 * publicada.
 */

export const COA_STATUSES: readonly CoaStatus[] = ["A", "B", "C", "D"];
export const COA_ACTIONS: readonly CoaAction[] = ["keep", "renumber", "create_mirror", "remove"];

/**
 * As cores da EF, validadas para daltonismo nos dois temas. A letra vai sempre
 * junto da cor: a cor sozinha não identifica o status.
 */
export const COA_STATUS_COLORS: Record<CoaStatus, string> = {
  A: "#2E8B57",
  B: "#B8860B",
  C: "#D0466A",
  D: "#6B67B8",
};

export interface ChartOfAccountsFrame {
  chartOfAccounts: string | undefined;
  accounts: number;
  byStatus: Record<CoaStatus, number>;
  /** A e B contra A, B e C, em %. `null` sem conta que migre: nunca vira 0. */
  adherencePct: number | null;
  byAction: Record<CoaAction, number>;
  /** `suggestion` enquanto nenhuma decisão foi salva: as barras mostram a sugestão. */
  actionBasis: "curation" | "suggestion";
  curationSavedAt: string | null;
}

function rec(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function count(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined;
}

/**
 * O envelope do quadro. Sem o domínio no bloco (backend anterior à P1), o quadro
 * fica fechado como "não coletado", e não some: a ausência é dita, não escondida.
 */
export function chartOfAccountsEnvelope(
  validation: DiagnosticSnapshot["validation"] | undefined,
): SectionEnvelope {
  const envelope = validation?.chart_of_accounts;
  if (envelope) return envelope;
  return {
    code: "NO_PRODUCER_RUN",
    state: "not_collected",
    reason: "",
    source: { kind: "domain", ref: "chart_of_accounts", snapshot: "validation" },
    data: {},
  } as SectionEnvelope;
}

/** Os números do quadro, ou `null` quando o envelope não está pronto ou veio torto. */
export function chartOfAccountsFrame(
  validation: DiagnosticSnapshot["validation"] | undefined,
): ChartOfAccountsFrame | null {
  const envelope = validation?.chart_of_accounts;
  if (!envelope || envelope.state !== "ok") return null;
  const data = rec(envelope.data);
  const statusData = rec(data.by_status);
  const actionData = rec(data.by_action);
  const accounts = count(data.accounts);
  const byStatus = {} as Record<CoaStatus, number>;
  for (const status of COA_STATUSES) {
    const value = count(statusData[status]);
    if (value === undefined) return null;
    byStatus[status] = value;
  }
  const byAction = {} as Record<CoaAction, number>;
  for (const action of COA_ACTIONS) {
    const value = count(actionData[action]);
    if (value === undefined) return null;
    byAction[action] = value;
  }
  if (accounts === undefined) return null;
  const adherence = data.adherence_pct;
  return {
    chartOfAccounts: typeof data.chart_of_accounts === "string" ? data.chart_of_accounts : undefined,
    accounts,
    byStatus,
    adherencePct: typeof adherence === "number" && Number.isFinite(adherence) ? adherence : null,
    byAction,
    actionBasis: data.action_basis === "curation" ? "curation" : "suggestion",
    curationSavedAt: typeof data.curation_saved_at === "string" ? data.curation_saved_at : null,
  };
}

/**
 * A conta final no YCOA como a §8.1 a mostra: em Manter, "igual YCOA" com o número
 * da conta YCOA; em Remover, nada (a tela mostra o travessão).
 */
export type FinalAccount =
  | { kind: "same_as_ycoa"; account: string }
  | { kind: "none" }
  | { kind: "account"; account: string };

export function finalAccount(item: CoaDiagnosticItem): FinalAccount {
  if (item.effective_decision === "remove") return { kind: "none" };
  const final = item.effective_final_account ?? undefined;
  if (item.effective_decision === "keep") {
    const ycoa = item.ycoa_account ?? final;
    return ycoa ? { kind: "same_as_ycoa", account: ycoa } : { kind: "none" };
  }
  return final ? { kind: "account", account: final } : { kind: "none" };
}

/** Percentual com uma casa só quando ela existe: 60 sai "60%", 10,93 sai "10,9%". */
export function formatPercent(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${formatCount(rounded, { decimals: Number.isInteger(rounded) ? 0 : 1 })}%`;
}
